import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import SmartImg from '../../components/ui/SmartImg';
import HorseActivity, { HorseStatus } from '../../components/horse/HorseActivity';

// Search is client-side on purpose. /horses already returns the barn's whole
// list in one query (tens of rows), so filtering in memory is instant and
// avoids a request per keystroke. If a barn ever gets large enough for that to
// hurt, this becomes a server-side query — the UI wouldn't change.
function matches(horse, terms) {
  const hay = [horse.name, horse.show_name, horse.breed, horse.color, horse.sex, horse.stall]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  // Every term must appear somewhere, so "bay gelding" narrows rather than widens.
  return terms.every((t) => hay.includes(t));
}

export default function HorsesList() {
  const { activeRole } = useAuth();
  const canAdd = activeRole === 'owner' || activeRole === 'manager';
  const [q, setQ] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['horses'],
    queryFn: () => api.get('/horses'),
  });

  // A second request rather than more columns on the first. The names and
  // stalls are what somebody came here for and they paint immediately; the
  // snapshots fill in behind them a moment later. Folding these aggregates
  // into /horses would also have slowed every horse picker in the app, none
  // of which show any of this.
  const activityQ = useQuery({
    queryKey: ['horse-activity'],
    queryFn: () => api.get('/horses/activity'),
    retry: false,
  });
  const activity = activityQ.data?.activity ?? {};
  const today = activityQ.data?.today ?? null;

  const horses = data?.horses ?? [];
  const terms = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = terms.length === 0 ? horses : horses.filter((h) => matches(h, terms));
  const searching = terms.length > 0;

  return (
    <div className="max-w-7xl mx-auto p-4 pb-24 sm:pb-8">
      <header className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Horses</h1>
        {canAdd && <Link to="/horses/new" className="btn-primary shrink-0">Add horse</Link>}
      </header>

      {horses.length > 0 && (
        <div className="mb-4">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">
              <SearchIcon />
            </span>
            <input
              type="search"
              className="input w-full pl-10 pr-10"
              placeholder="Search name, stall, breed, color…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              // No autoFocus: on a phone it would throw up the keyboard and
              // hide the list every time you open the page.
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              aria-label="Search horses"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                aria-label="Clear search"
                className="absolute right-0 inset-y-0 px-3 flex items-center text-ink-400 hover:text-ink-700"
              >
                ✕
              </button>
            )}
          </div>
          {searching && (
            <p className="text-xs text-ink-400 mt-1.5" role="status" aria-live="polite">
              {filtered.length} of {horses.length}
            </p>
          )}
        </div>
      )}

      {isLoading && <p className="text-ink-400">Loading…</p>}
      {isError && <p className="text-red-600">Failed to load horses.</p>}

      {!isLoading && horses.length === 0 && (
        <div className="card p-8 text-center text-ink-400">
          No horses yet.{canAdd ? ' Add your first one.' : ''}
        </div>
      )}

      {searching && filtered.length === 0 && (
        <div className="card p-8 text-center text-ink-500">
          <p>No horse matches “{q.trim()}”.</p>
          <button onClick={() => setQ('')} className="text-sm text-ink-600 underline mt-2">
            Clear search
          </button>
        </div>
      )}

      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((h) => (
          <li key={h.id}>
            <Link
              to={`/horses/${h.id}`}
              className="card block p-4 hover:bg-ink-50 transition-colors h-full"
            >
              <div className="flex items-center gap-3">
                {h.photo_url ? (
                  <SmartImg src={h.photo_url} width={48} className="w-12 h-12 rounded-full object-cover" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-ink-100 flex items-center justify-center text-ink-400 font-mono text-xs">
                    {h.name?.[0] ?? '?'}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">
                    {h.name}
                    {h.stall && <span className="text-ink-400 text-sm ml-2">stall {h.stall}</span>}
                  </div>
                  <div className="text-sm text-ink-500 truncate">
                    {[h.breed, h.color, h.sex].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>

              <HorseStatus snapshot={activity[h.id]} today={today} />

              <HorseActivity snapshot={activity[h.id]} today={today} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}
